import React from 'react';
import isEmpty from 'lodash/isEmpty';
import average from 'utils/average';
import StatItem from 'components/dashboard/StatItem';
import ExpansiveProgressMarker from 'components/pure/utils/ExpansiveProgressMarker';
import './StatTabContent.styl';
import BasedOnDatesLink from 'components/dashboard/common/BasedOnDatesLink';
import {maxByReplacingNaN, sortByReplacingNaN} from 'utils/NaN';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import {TeamIcon} from '@components/icon/TeamIcon';

/**
 * Returns the active tab. Tabs may or may not have subtabs, if subtabs exist
 * then check whether activeSubtabId is present. If it isn't then default to the
 * subtab with the same name as its parent.
 *
 */
function getTab(tabConfigs, activeTabId, activeSubtabId) {
  const tab = tabConfigs[activeTabId];

  if (isEmpty(tab.subtabs)) return [activeTabId, tab];

  const subtab = tab.subtabs[activeSubtabId];
  return subtab ? [activeSubtabId, subtab] : [activeTabId, tab.subtabs[activeTabId]];
}

export default class StatsTabContent extends React.Component {
  state = {
    secondaryFilterId: undefined,
    loadingSecondaryFilterData: false,
    customSecondaryFilteredStats: undefined,
    customSecondaryColor: undefined,
  };

  async loadSecondaryFilterStats(
    secondaryDimensionFilter,
    secondaryDimensionColorFromOption,
    id,
    direction,
    startDate,
    endDate,
    teamIds
  ) {
    this.setState({loadingSecondaryFilterData: true});
    const {fetchMetricSource, activeTabId} = this.props;

    const filteredStats = await secondaryDimensionFilter({
      secondaryDimension: id,
      direction,
      teamIds,
      startDate,
      endDate,
      fetchMetricSource,
      metric: activeTabId,
    });

    const color = secondaryDimensionColorFromOption
      ? secondaryDimensionColorFromOption(id)
      : undefined;

    this.setState({
      secondaryFilterId: id,
      loadingSecondaryFilterData: false,
      customSecondaryFilteredStats: filteredStats,
      customSecondaryColor: color,
    });
  }

  render() {
    const {
      tabConfigs,
      activeTabId,
      stats: originalStats,
      fetchTrendData,
      getLinkAndName,
      color: originalColor,
      trendComponent,
      startDate,
      endDate,
      direction,
      teamIds,
      location,
      manager,
      activeSubtabId,
      selectSubtab,
      descriptions,
      chartLabelFn,
      onOpenDrillDown,
      emptyMessage = 'No reps matching those criteria',
      showAverage = true,
    } = this.props;

    const {
      secondaryFilterId,
      loadingSecondaryFilterData,
      customSecondaryFilteredStats,
      customSecondaryColor,
    } = this.state;

    const stats = customSecondaryFilteredStats || originalStats;
    const color = customSecondaryColor || originalColor;

    const [tabId, tab] = getTab(tabConfigs, activeTabId, activeSubtabId);
    if (!tab) {
      console.error('cant find tab', activeTabId, activeSubtabId);
      return null;
    }

    const values = stats.map(s => s.value);
    const max = maxByReplacingNaN(values);
    const formatter = tab.formatValue;
    const averageMarkerOrNull = showAverage
      ? averageMarker(values, tab.averagesShouldIncludeZeroes, max, formatter)
      : null;
    const description = (descriptions && descriptions[tabId]) || tab.description;

    // (see tabConfigs.js - meetings specifically).
    // we allow for a secondary filter by providing the filter options, option renderer, and
    // a hook to invoke the secondary filter fetch.
    const secondaryDimensionOptions =
      tab.secondaryDimensionOptions && tab.secondaryDimensionOptions();
    const SecondaryDimensionOptionRenderer = tab.secondaryDimensionOptionRenderer;

    const secondaryDimensionFilter = tab.secondaryDimensionFilter;

    return (
      <div className="rep-stat-tab-content">
        {loadingSecondaryFilterData && <LoadingOverlay />}

        <Subtabs
          subtabs={tabConfigs[activeTabId].subtabs}
          activeSubtabId={tabId}
          selectSubtab={selectSubtab}
        />
        <div className="info-top">
          <div>
            <h2>{tab.label}</h2>
            <div className="description">{description}</div>
          </div>
          {secondaryDimensionOptions && secondaryDimensionOptions.size > 0 && (
            <SecondaryDimensionOptionRenderer
              className="secondary-dimension-renderer"
              options={secondaryDimensionOptions}
              value={secondaryFilterId}
              onSelect={({id}) =>
                this.loadSecondaryFilterStats(
                  secondaryDimensionFilter,
                  tab.secondaryDimensionColorFromOption,
                  id,
                  direction,
                  startDate,
                  endDate,
                  teamIds
                )
              }
            />
          )}
        </div>
        <BasedOnDatesLink
          startDate={this.props.headerStartDate}
          endDate={this.props.headerEndDate}
          location={location}
        />
        {stats.size === 0 ? (
          <p>{emptyMessage}</p>
        ) : (
          <div className="bar-container">
            {averageMarkerOrNull}
            {sortByReplacingNaN(stats, stats => stats.value)
              .reverse()
              .map((stat, i) => (
                <StatItem
                  color={color}
                  trendColor={originalColor}
                  formatValue={formatter}
                  activeTabId={tabId}
                  key={i}
                  stat={stat}
                  max={max}
                  getLinkAndName={getLinkAndName}
                  fetchTrendData={fetchTrendData ? () => fetchTrendData(stat) : null}
                  trendComponent={trendComponent}
                  startDate={startDate}
                  endDate={endDate}
                  manager={manager}
                  title={tab.chartTitle}
                  chartLabelFn={chartLabelFn}
                  onOpenDrillDown={onOpenDrillDown}
                />
              ))}
          </div>
        )}
      </div>
    );
  }
}

function Subtabs({subtabs, activeSubtabId, selectSubtab}) {
  if (isEmpty(subtabs)) return null;

  return (
    <div className="subtabs">
      {Object.entries(subtabs).map(([id, tab]) => (
        <span
          key={id}
          onClick={() => selectSubtab(id)}
          className={`subtab ${activeSubtabId === id && 'active'}`}>
          {tab.label}
        </span>
      ))}
    </div>
  );
}

/**
 * Render the average for all rows.
 *
 * @param values all values to render (used to find the average).
 * @param averagesShouldIncludeZeroes should zeroes be included in the average? If false, zeroes
 * are not included in the denominator when calculating the average.
 * @param max the max of all values.
 * @param formatter function to call to format the average as a string.
 */
function averageMarker(values, averagesShouldIncludeZeroes, max, formatter) {
  const teamAvg =
    average(
      values.filter(v => !Number.isNaN(v)).filter(v => averagesShouldIncludeZeroes || v !== 0)
    ) || 0;
  return (
    <ExpansiveProgressMarker
      percentage={(teamAvg / max) * 100}
      tooltip={`Team Average ${formatter(teamAvg)}`}
      label={TeamIcon}
    />
  );
}
